// ============================================================
// Root.tsx — the very first fork (src/index.tsx renders this): a PUBLIC
// e-signature address (sign.casevo.co.uk, /sign/…, /verify — see
// utils/esignRoute) renders the public shell INSTEAD of the app, before
// App.tsx decides login vs app, so nobody is asked to sign in and no
// session / presence code runs. The shell is lazy-loaded: the app's own
// bundle barely grows.
// (E-sign audit fixes A) A signing link's token is in the URL fragment
// (/s#<token>) — read here, and again if only the fragment changes (a second
// link pasted into the same tab doesn't reload the page).
// ============================================================
import React, { Suspense, useEffect, useState } from 'react'
import App from './App'
import { publicEsignRoute } from './utils/esignRoute'

const PublicEsignApp = React.lazy(() => import('./components/esign/PublicEsignApp'))

// Only on a signing page (the staff app never re-renders for an in-page #anchor).
const useHash = (initial: string | undefined, watch: boolean): string => {
  const [hash, setHash] = useState(initial ?? window.location.hash)
  useEffect(() => {
    if (initial !== undefined || !watch) return undefined
    const on = (): void => setHash(window.location.hash)
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [initial, watch])
  return hash
}

const Root: React.FC<{ hostname?: string; pathname?: string; hash?: string }> = ({ hostname = window.location.hostname, pathname = window.location.pathname, hash: hashProp }) => {
  const hash = useHash(hashProp, publicEsignRoute(hostname, pathname)?.page === 'sign')
  const route = publicEsignRoute(hostname, pathname, hash)
  if (route) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-casevo-offwhite" />}>
        <PublicEsignApp route={route} hostname={hostname} />
      </Suspense>
    )
  }
  return <App />
}

export default Root
